// src/ui/morphMotion.js
//
// Shared choreography for the modal ↔ corner-toast morph (bulk-action
// "Run in background"). Pure style builders so they unit-test without a DOM.
import { MODAL_MOTION, prefersCoarsePointer } from './overlayMotion';
import { foundation } from './tokens';
import { FLOATING_ABOVE_TAB_BOTTOM_CSS } from '../components/layout/BottomTabBar';

// `framer-motion/dom` is ~287KB and is the single biggest non-first-paint weight
// in the cold-start `main` bundle. It powers ONLY the optional bulk "morph into
// pill" spring (runFlipSpringMorph) — everything else in this module is pure.
// So we lazy-load `animate` here, keeping framer-motion OUT of `main` (it loads
// in its own chunk on idle / first use). Warmed via warmFlipSpringMorph() so the
// chunk is ready before the user's first morph; if it somehow hasn't landed,
// runFlipSpringMorph degrades to an instant (no-morph) close — never blocks.
let animateFn = null;
let animateLoad = null;
function loadAnimate() {
  if (animateFn) return Promise.resolve(animateFn);
  if (!animateLoad) {
    animateLoad = import('framer-motion/dom')
      .then((m) => { animateFn = m.animate; return animateFn; })
      // Reset on failure (e.g. offline) so a later attempt can retry; callers
      // keep degrading gracefully to no-morph until it lands.
      .catch(() => { animateLoad = null; return null; });
  }
  return animateLoad;
}

/**
 * Prewarm the framer-motion chunk (idempotent, fire-and-forget). Call on idle
 * after first paint so the first bulk-morph animates instead of degrading.
 */
export function warmFlipSpringMorph() { loadAnimate(); }

/** Duration for modal ↔ background-pill FLIP morph (forward + reverse). */
export function bulkBackgroundMorphDurationMs(isTouch = prefersCoarsePointer()) {
  return isTouch ? 280 : 360;
}

/** Opacity crossfade window as a fraction of morph duration (forward inner content). */
export const BULK_MORPH_CROSSFADE_RATIO = 0.5;

/** Reverse morph: panel fades in over the back portion (pill → modal). */
export const BULK_MORPH_PANEL_REVEAL_RATIO = 0.65;

export function bulkMorphEasing(isTouch = prefersCoarsePointer()) {
  return isTouch ? MODAL_MOTION.easingMobile : MODAL_MOTION.easing;
}

export function readDialogRectForMorph(el) {
  if (!el) return undefined;
  const r = el.getBoundingClientRect();
  if (r.width <= 0 || r.height <= 0) return undefined;
  return { top: r.top, left: r.left, width: r.width, height: r.height };
}

/**
 * Plain morph rect from a DOM node OR an already-measured DOMRect-like.
 * Shared by the customer-card forward/reverse FLIP (identity strip ↔ pick row).
 */
export function snapshotMorphRect(elOrRect) {
  if (!elOrRect) return undefined;
  if (typeof elOrRect.getBoundingClientRect === 'function') {
    return readDialogRectForMorph(elOrRect);
  }
  const { top, left, width, height } = elOrRect;
  if (!(width > 0 && height > 0)) return undefined;
  return { top, left, width, height };
}

/** Live measure with optional last-known rect from {@link useDialogMorphRectCache}. */
export function resolveDialogRectForMorph(el, cachedRect) {
  return readDialogRectForMorph(el) ?? cachedRect ?? undefined;
}

/**
 * Capture dialog rect after layout settles (double-rAF). Returns undefined when
 * the element is missing or has zero size — caller should fall back to corner crossfade.
 */
export function captureDialogRectForMorph(el) {
  return new Promise((resolve) => {
    if (!el) {
      resolve(undefined);
      return;
    }
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        const r = el.getBoundingClientRect();
        if (r.width <= 0 || r.height <= 0) {
          resolve(undefined);
          return;
        }
        resolve({
          top: r.top,
          left: r.left,
          width: r.width,
          height: r.height,
        });
      });
    });
  });
}

export function warnMissingMorphOrigin(context = 'bulk') {
  if (process.env.NODE_ENV === 'production') return;
  // eslint-disable-next-line no-console
  console.warn(`[CrewVex bulk-morph] ${context}: dialog rect capture failed — toast will use corner crossfade.`);
}

const { spacePx: sp } = foundation;

/**
 * Resting placement of the background toast, expressed for a full-width flex wrapper
 * so the pill's own `transform` stays free for the morph. Touch centres the pill above
 * the floating bottom-nav pill; desktop tucks it into the bottom-right corner.
 */
export function computeToastAnchor({ isTouch }) {
  return {
    bottom: isTouch ? FLOATING_ABOVE_TAB_BOTTOM_CSS : sp[4],
    justify: isTouch ? 'center' : 'flex-end',
    sideInset: sp[4],
  };
}

/**
 * Modal panel exit when flying into the background toast. The centered panel collapses
 * toward the toast's resting spot and shrinks; exact landing is masked by the toast fading
 * in over it. Touch collapses straight DOWN to bottom-centre (mirrors the centred pill);
 * desktop collapses toward the bottom-right corner. Forward only — enter is never overridden.
 */
export function modalCornerExitStyle({ reduced, durationMs, isTouch = false, easing = MODAL_MOTION.easing }) {
  if (reduced) {
    return { opacity: 0, transition: `opacity ${durationMs}ms ${easing}`, willChange: 'opacity' };
  }
  return {
    opacity: 0,
    transform: isTouch
      ? 'translate3d(0, 40vh, 0) scale(0.14)'
      : 'translate3d(34vw, 40vh, 0) scale(0.14)',
    transformOrigin: isTouch ? '50% 100%' : '100% 100%',
    transition: `opacity ${durationMs}ms ${easing}, transform ${durationMs}ms ${easing}`,
    willChange: 'transform, opacity',
    backfaceVisibility: 'hidden',
  };
}

/**
 * Background-toast enter/exit. `visible` true = resting; false = collapsed toward the
 * viewport centre (where the modal lives). One style carries the spatial travel BOTH ways:
 * enter flies centre→rest, exit flies rest→centre. Touch travels purely on the Y axis
 * (the pill is centred); desktop also drifts on X toward the corner.
 */
export function toastCornerMotionStyle(visible, { reduced, durationMs, isTouch = false, easing = MODAL_MOTION.easing }) {
  if (reduced) {
    return {
      opacity: visible ? 1 : 0,
      transition: `opacity ${durationMs}ms ${easing}`,
      willChange: visible ? 'opacity' : 'auto',
    };
  }
  const hidden = isTouch
    ? 'translate3d(0, -34vh, 0) scale(0.35)'
    : 'translate3d(-32vw, -34vh, 0) scale(0.35)';
  return {
    opacity: visible ? 1 : 0,
    transform: visible ? 'translate3d(0, 0, 0) scale(1)' : hidden,
    transformOrigin: isTouch ? '50% 100%' : '100% 100%',
    transition: `opacity ${durationMs}ms ${easing}, transform ${durationMs}ms ${easing}`,
    willChange: visible ? 'transform, opacity' : 'auto',
    backfaceVisibility: 'hidden',
  };
}

// ── True shared-element morph (FLIP) ────────────────────────────────────────
// Animate transform only (GPU-composited) — iOS-grade smoothness on Safari.
// The element is laid out at its destination rect; an invert transform makes
// it appear at the origin rect, then a spring settles to identity.

/** Invert transform: element at `to` rect visually matches `from` rect. */
export function computeFlipInvertTransform(from, to) {
  if (!from || !to || to.width <= 0 || to.height <= 0) {
    return 'translate3d(0,0,0) scale(1)';
  }
  const scaleX = from.width / to.width;
  const scaleY = from.height / to.height;
  const tx = from.left - to.left;
  const ty = from.top - to.top;
  return `translate3d(${tx}px, ${ty}px, 0) scale(${scaleX}, ${scaleY})`;
}

const FLIP_IDENTITY = 'translate3d(0,0,0) scale(1)';

/** Spring tuned to feel close to UIView spring (slight ease, no rubber-band). */
export function bulkMorphSpringConfig(isTouch = prefersCoarsePointer()) {
  return isTouch
    ? { type: 'spring', stiffness: 420, damping: 40, mass: 0.8, restDelta: 0.35, restSpeed: 0.5 }
    : { type: 'spring', stiffness: 360, damping: 38, mass: 0.85, restDelta: 0.35, restSpeed: 0.5 };
}

/**
 * Run a FLIP spring on `el` (already positioned at `toRect`). Returns stop fn.
 * @param {boolean} [opts.reduced] — skip animation
 */
export function runFlipSpringMorph(el, fromRect, toRect, { isTouch, reduced, onComplete } = {}) {
  if (!el || reduced) {
    onComplete?.();
    return () => {};
  }
  // Lazy framer-motion: if the chunk hasn't landed yet, kick off the load and
  // degrade to an instant (no-morph) complete for THIS call — same graceful
  // shape as the reduced-motion path. Never blocks the modal close.
  if (!animateFn) {
    loadAnimate();
    onComplete?.();
    return () => {};
  }
  const invert = computeFlipInvertTransform(fromRect, toRect);
  el.style.transformOrigin = '0 0';
  el.style.transform = invert;
  const controls = animateFn(el, { transform: FLIP_IDENTITY }, bulkMorphSpringConfig(isTouch));
  controls.finished
    .then(() => onComplete?.())
    .catch(() => {});
  return () => controls.stop();
}

/** Absolute box for a fixed-position morph surface. */
export function morphSurfaceBox(rect) {
  return {
    top: `${rect.top}px`,
    left: `${rect.left}px`,
    width: `${rect.width}px`,
    height: `${rect.height}px`,
  };
}

/** Transition string for the surface's box (geometry + radius), one easing. */
export function morphSurfaceTransition({ durationMs, easing = MODAL_MOTION.easing }) {
  return ['top', 'left', 'width', 'height', 'border-radius']
    .map((p) => `${p} ${durationMs}ms ${easing}`)
    .join(', ');
}

/**
 * Modal panel exit for the true morph: fade IN PLACE (no travel). The visible
 * travel/resize is owned entirely by the morph surface painted on top, so the
 * panel must NOT also fly to the corner (that reads as two elements). Slightly
 * faster than the morph so the content is gone before the box reaches the pill.
 */
export function modalFadeExitStyle({ reduced, durationMs, easing = MODAL_MOTION.easing }) {
  const ms = reduced ? durationMs : Math.round(durationMs * 0.6);
  return { opacity: 0, transition: `opacity ${ms}ms ${easing}`, willChange: 'opacity' };
}
